import { useVersion } from "@utils/hooks/useVersion";
import { useRef, useState, useContext, useMemo } from "react";
import React from 'react';
import pitch1 from '@assets/dynamicColors/pitch/pitch_1.png';
import pitch2 from '@assets/dynamicColors/pitch/pitch_2.png';
import pitch3 from '@assets/dynamicColors/pitch/pitch_3.png';
import pitch4 from '@assets/dynamicColors/pitch/pitch_4.png';
import pitch5 from '@assets/dynamicColors/pitch/pitch_5.png';
import { useColorContext } from "@stores/ColorContext";
import { useTheme } from "@stores/ThemeContext";
import { T } from "@utils/enums";
import { IState, StateContext } from "../../../../../state/stateContext";
import { IPlayerInLines } from "./PlayersInLines";

interface IFootballFieldProps {
    homeFormation: string | null;
    awayFormation: string | null;
    homeFormationType?: string | null;
    awayFormationType?: string | null;
    homeLines?: IPlayerInLines[][];
    awayLines?: IPlayerInLines[][];
    children?: React.ReactNode;
}

export interface FieldContainerRefContext {
    fieldContainerRef: React.RefObject<HTMLDivElement> | null;
}

export const FieldContainerRefContext = React.createContext<FieldContainerRefContext>({ fieldContainerRef: null });

const FootballField = ({ homeFormation, awayFormation, homeFormationType, awayFormationType, homeLines, awayLines, children }: IFootballFieldProps) => {
    const fieldContainerRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLDivElement>(null);
    const homeHalfRef = useRef<HTMLDivElement>(null);
    const awayHalfRef = useRef<HTMLDivElement>(null);
    const homePenaltyBox = useRef<HTMLDivElement>(null);
    const awayPenaltyBox = useRef<HTMLDivElement>(null);
    const { isV1, isV2 } = useVersion();
    const ctx = useContext(StateContext);
    const { setStContext } = ctx || {};

    const homeHalfHeight = useMemo(() => {
        let homeFormationLength = homeFormation?.split('-').length;
        const homeLinesCount = homeLines && homeLines.length > 0 ? homeLines.length - 1 : null;
        
        const defaultLinesCount = 4;
        let linesCount = homeFormationLength || (homeLinesCount ?? defaultLinesCount);
        
        if (homeFormationType === 'diamond') {
            linesCount += 2;
        }
        
        const freeHomeStartSpace = 56;
        const constHomeValue = 20;
        const playerHeight = 76;
        return (playerHeight * linesCount) + freeHomeStartSpace + constHomeValue;
    }, [homeLines, homeFormation, homeFormationType]);

    const awayHalfHeight = useMemo(() => {
        let awayFormationLength = awayFormation?.split('-').length;
        const awayLinesCount = awayLines && awayLines.length > 0 ? awayLines.length - 1 : null;
        
        const defaultLinesCount = 4;
        let linesCount = awayFormationLength || (awayLinesCount ?? defaultLinesCount);
        
        if (awayFormationType === 'diamond') {
            linesCount += 2;
        }
        
        const freeAwayStartSpace = 0;
        const constAwayValue = -31;
        const playerHeight = 76;
        return (playerHeight * linesCount) + freeAwayStartSpace + constAwayValue;
    }, [awayLines, awayFormation, awayFormationType]);

    const containerDimensions = useMemo(() => {
        if (isV2) {
            //  For V2: sum of all 5 parts of the field
            // home-half: 117px (constant)
            // home-half-2: homeHalfHeight - 117 - 50
            // circle: 100px (constant)
            // away-half-2: awayHalfHeight - 118 + 50
            // away-half: 118px (constant)
            const totalHeight = 117 + (homeHalfHeight - 117 - 50) + 100 + (awayHalfHeight - 118 + 50) + 118;
            return {
                height: `${totalHeight}px`,
                width: '331px'
            };
        } else {
            // For V1: homeHalfHeight + 100px (circle) + awayHalfHeight
            const totalHeight = homeHalfHeight + 100 + awayHalfHeight;
            return {
                height: `${totalHeight}px`,
                width: '331px'
            };
        }
    }, [homeHalfHeight, awayHalfHeight, isV2]);

    const handleFieldClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('[data-testid^="one-player"]')) {
            setStContext((prev: IState) => ({
                ...prev,
                activeTooltipPlayerId: null
            }));
        }
    };

    const FootballFieldV1 = () => {
        return (
            <div data-testid="football-field-container" ref={fieldContainerRef} 
                className="w-full bg-footbal-field-light dark:bg-footbal-field-dark
                            pt-[58px] px-[12px] pb-[12px] flex justify-center items-center rounded-b-[4px] mb-[8px]"
                onClick={handleFieldClick}>
                <div data-testid="football-field" ref={fieldRef} className="w-[327px] border-2 border-monoY border-opacity-20">
                    
                    <div data-testid="football-field-home-half" ref={homeHalfRef}
                        className={`w-full border-b-2 border-monoY border-opacity-20`}
                        style={{ minHeight: `${homeHalfHeight}px`, height: `${homeHalfHeight}px` }}>
                        <div data-testid="football-field-home-penalty-box" ref={homePenaltyBox} className="w-full h-auto flex flex-col justify-center items-center bg-transparent">
                            <div data-testid="football-field-home-penalty-box-content" className="w-[203px] h-[84px] border-l-2 border-r-2 border-b-2 border-monoY 
                                            border-opacity-20 flex flex-col justify-start items-center">
                                <div data-testid="football-field-home-penalty-box-content-line" className="w-[97px] h-[30px] border-r-2 border-l-2 border-b-2 border-monoY border-opacity-20" />

                            </div>
                            <div data-testid="football-field-home-penalty-box-content-circle" className="w-full h-[100px] flex items-start justify-center overflow-hidden">
                                <div data-testid="football-field-home-penalty-box-content-circle-circle" className="w-[92px] h-[92px] rounded-full border-2 border-monoY border-opacity-20 -mt-[74px]" />
                            </div>
                        </div>
                    </div>
                    <div data-testid="football-field-home-penalty-box-content-circle-circle" className="relative top-0 w-full h-[100px] flex items-start justify-center">
                        <div className="w-[92px] h-[92px] rounded-full border-2 border-monoY border-opacity-20 -mt-[46px] " />
                    </div>
                    <div data-testid="football-field-away-half" ref={awayHalfRef}
                        className={`w-full border-monoY border-opacity-20`}
                        style={{ minHeight: `${awayHalfHeight}px`, height: `${awayHalfHeight}px` }}>
                        <div data-testid="football-field-away-penalty-box" ref={awayPenaltyBox} className="w-full h-full flex flex-col justify-end items-center bg-transparent">

                            <div data-testid="football-field-away-penalty-box-content-circle" className="w-full h-[100px] flex items-end justify-center overflow-hidden">
                                <div data-testid="football-field-away-penalty-box-content-circle-circle" className="w-[92px] h-[92px] rounded-full border-2 border-monoY border-opacity-20 -mb-[74px]" />
                            </div>
                            <div data-testid="football-field-away-penalty-box-content-line" className="w-[203px] h-[84px] border-l-2 border-r-2 border-t-2 border-monoY
                                            border-opacity-20 flex flex-col justify-end items-center">
                                <div data-testid="football-field-away-penalty-box-content-line-line" className="w-[97px] h-[30px] border-r-2 border-l-2 border-t-2 border-monoY border-opacity-20 " />

                            </div>
                        </div>
                    </div>
                </div>
               
            </div>
        )
    }
    
    const FootballFieldV2FiveParts = () => {
        const [showBorders, setShowBorders] = useState(false);
        const { colors } = useColorContext();
        const {theme} = useTheme();

        return (
            <div data-testid="football-field-container" ref={fieldContainerRef} 
                className="w-full
                            pt-[46px] pb-[12px] flex justify-center items-center"
                            style={{ backgroundColor: theme === T.LIGHT ? '#FFFFFF' : '#222222'}}
                            onClick={handleFieldClick}>
                <div data-testid="football-field" ref={fieldRef} className="w-full max-w-[351px] relative"
                     style={{ 
                         border: showBorders ? '2px solid rgba(255, 255, 255, 0.2)' : 'none'
                     }}>
                    
                    <div data-testid="football-field-home-half" ref={homeHalfRef}
                        className={`w-full relative`}
                        style={{ 
                            minHeight: `${117}px`, 
                            height: `${117}px`,
                            borderBottom: showBorders ? '2px solid rgba(255, 255, 255, 0.2)' : 'none',
                            backgroundImage: `url(${pitch1})`,
                            backgroundSize: '100% 100%',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }}>
                       
                    </div>
                    <div data-testid="football-field-home-half-2" ref={homeHalfRef}
                        className={`w-full relative`}
                        style={{ 
                            minHeight: `${homeHalfHeight-117-50}px`, 
                            height: `${homeHalfHeight-117-50}px`,
                            borderBottom: showBorders ? '2px solid rgba(255, 255, 255, 0.2)' : 'none',
                            backgroundImage: `url(${pitch2})`,
                            backgroundSize: '100% 100%',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }}>
                       
                    </div>
                    
                    <div data-testid="football-field-home-penalty-box-content-circle-circle" className="relative top-0 w-full h-[100px] flex items-start justify-center"
                         style={{
                             backgroundImage: `url(${pitch3})`,
                             backgroundSize: '100% 100%',
                             backgroundPosition: 'center',
                             backgroundRepeat: 'no-repeat'
                         }}>
                    </div>
                    <div data-testid="football-field-away-half-2" ref={awayHalfRef}
                        className={`w-full relative`}
                        style={{ 
                            minHeight: `${awayHalfHeight-118+50}px`, 
                            height: `${awayHalfHeight-118+50}px`,
                            backgroundImage: `url(${pitch4})`,
                            backgroundSize: '100% 100%',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }}>
                    </div>
                    <div data-testid="football-field-away-half" ref={awayHalfRef}
                        className={`w-full relative`}
                        style={{ 
                            minHeight: `${118}px`, 
                            height: `${118}px`,
                            backgroundImage: `url(${pitch5})`,
                            backgroundSize: '100% 100%',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }}>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
        <FieldContainerRefContext.Provider value={{ fieldContainerRef }}>
            {isV1 ? <FootballFieldV1 /> : <FootballFieldV2FiveParts />}
            <div data-testid="football-field-container-children" 
            className={`absolute top-[48px] left-0 w-full z-10 flex justify-center items-center `}>
                <div data-testid="football-field-container-children-content" className="" style={containerDimensions}>
                    {children}
                </div>
            </div>
        </FieldContainerRefContext.Provider>
        </>
    )
}

export default FootballField;